/**
 * «Νέα εμφάνιση» — παράθυρο ρέστων για τα μετρητά (απόφαση ιδιοκτήτη 12/10/2026): ανοίγει ΠΑΝΤΑ
 * με το «Μετρητά», όπως το παλιό αριθμητικό της ολοκλήρωσης: «Ακριβές», +5/+10/+20/+50 € που
 * ΠΡΟΣΘΕΤΟΥΝ στο «Δόθηκαν», αριθμητικό για άλλο ποσό, ρέστα μεγάλα, και ολοκλήρωση ΜΟΝΟ με την
 * «Πληρωμή» — ο ταμίας βλέπει τα ρέστα πριν κλείσει η παραγγελία.
 *
 * Το ποσό είναι string όπως στο cashReceived του Take Away ('' = δεν δόθηκε ποσό → πληρωμή χωρίς
 * ρέστα, όπως πριν). Μόνο εμφάνιση: την πληρωμή την κάνει ο γονέας στο onPay.
 */
import React, { useEffect } from 'react';
import { useBackdropDismiss } from '../../utils/backdropDismiss';
import { useTranslation } from 'react-i18next';
import { Delete, X } from 'lucide-react';
import { formatEuro } from '../../utils/formatEuro';

interface CashChangeSheetProps {
  total: number;
  value: string;
  onChange: (next: string) => void;
  onPay: () => void;
  onClose: () => void;
  /** Εκπαιδευτική παραγγελία (κράτημα 2″ στο «Μετρητά»): το κουμπί πληρωμής γίνεται πορτοκαλί. */
  training?: boolean;
}

const QUICK = [5, 10, 20, 50];

const CashChangeSheet: React.FC<CashChangeSheetProps> = ({ total, value, onChange, onPay, onClose, training = false }) => {
  const { t } = useTranslation(['takeaway', 'common']);
  const given = value === '' ? null : Number(value);
  const enough = given === null || given + 0.001 >= total;
  const change = given !== null && enough ? Math.round((given - total) * 100) / 100 : null;
  const missing = given !== null && !enough ? Math.round((total - given) * 100) / 100 : null;

  const press = (key: string) => {
    if (key === 'back') { onChange(value.slice(0, -1)); return; }
    if (key === '.') {
      if (!value.includes('.')) onChange((value || '0') + '.');
      return;
    }
    const [, decimals] = value.split('.');
    if (decimals !== undefined && decimals.length >= 2) return;
    onChange(value === '0' ? key : value + key);
  };
  const add = (amount: number) => onChange((Number(value || '0') + amount).toFixed(2));

  // Μόνο Esc από φυσικό πληκτρολόγιο: ψηφία + Enter στέλνει και ο σαρωτής barcode (ακούει όλο το
  // παράθυρο), και ένα σάρωμα εδώ θα γινόταν «Δόθηκαν» και πληρωμή.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Το φόντο κλείνει ΜΟΝΟ όταν και το πάτημα ξεκίνησε στο φόντο. Το click που στέλνει η οθόνη αφής
  // μετά το «Μετρητά» (πάτημα που ξεκίνησε στο κουμπί) δεν κλείνει πια το παράθυρο (15/10/2026).
  const backdrop = useBackdropDismiss(onClose);

  const key = 'h-14 rounded-xl text-xl font-semibold text-t1 bg-surface-1 border border-line hover:bg-surface-3 active:scale-95 transition-all';
  const quick = 'h-12 rounded-xl text-base font-semibold border transition-all active:scale-95';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      {...backdrop}
      data-testid="takeaway-cash-sheet"
    >
      <div className="w-full max-w-3xl rounded-2xl bg-surface-2 border border-line shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-line">
          <div>
            <h2 className="text-xl font-bold text-t1">{t('v2.cashTitle')}</h2>
            <p className="text-sm text-t3">{t('v2.cashHint')}</p>
          </div>
          <button type="button" onClick={onClose} aria-label={t('common:close', { defaultValue: 'Κλείσιμο' })}
            className="w-11 h-11 shrink-0 rounded-xl hover:bg-surface-3 flex items-center justify-center text-t3">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 p-5">
          <div className="space-y-3">
            <div className="rounded-xl bg-surface-1 border border-line p-4 space-y-1.5 tabular-nums">
              <div className="flex justify-between text-base"><span className="text-t3">{t('v2.total')}</span><span className="font-semibold text-t1">{formatEuro(total)}</span></div>
              <div className="flex justify-between text-base">
                <span className="text-t3">{t('v2.given')}</span>
                <span className="font-semibold text-t1" data-testid="cash-given">{given === null ? '—' : formatEuro(given)}</span>
              </div>
            </div>
            <div className={`rounded-xl p-4 text-center ${missing !== null ? 'bg-amber-50 dark:bg-amber-400/10' : 'bg-brand-soft'}`} data-testid="cash-change">
              <div className={`text-sm ${missing !== null ? 'text-amber-800 dark:text-amber-300' : 'text-brand'}`}>
                {missing !== null ? t('v2.missing') : t('v2.change')}
              </div>
              <div className={`text-4xl font-bold tabular-nums leading-tight ${missing !== null ? 'text-amber-800 dark:text-amber-300' : 'text-t1'}`}>
                {missing !== null ? formatEuro(missing) : change !== null ? formatEuro(change) : '—'}
              </div>
            </div>
            <button type="button" onClick={() => onChange(total.toFixed(2))}
              className={`${quick} w-full bg-surface-1 border-line text-t1 hover:bg-surface-3`}>
              {t('v2.exact')} · {formatEuro(total)}
            </button>
            <div className="grid grid-cols-4 gap-2">
              {QUICK.map(amount => (
                <button key={amount} type="button" onClick={() => add(amount)}
                  className={`${quick} bg-surface-1 border-line text-t1 hover:bg-surface-3`}>
                  +{amount} €
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 content-start">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0'].map(k => (
              <button key={k} type="button" onClick={() => press(k)} className={key}>{k === '.' ? ',' : k}</button>
            ))}
            <button type="button" onClick={() => press('back')} aria-label={t('v2.backspace')} className={`${key} flex items-center justify-center`}>
              <Delete className="h-6 w-6" />
            </button>
            <button type="button" onClick={() => onChange('')}
              className="col-span-3 h-11 rounded-xl text-sm font-medium text-t3 hover:bg-surface-3">
              {t('v2.clearAmount')}
            </button>
          </div>
        </div>

        <div className="flex gap-3 px-5 pb-5">
          <button type="button" onClick={onClose}
            className="flex-1 h-14 rounded-xl border border-line-strong text-t2 font-semibold hover:bg-surface-3">
            {t('back')}
          </button>
          <button type="button" onClick={onPay} disabled={!enough} data-testid="cash-pay"
            className={`flex-[2] h-14 rounded-xl font-semibold text-lg active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
              training ? 'bg-orange-500 text-white' : 'bg-brand text-brand-fg hover:brightness-110'
            }`}>
            {change !== null ? t('v2.payWithChange', { amount: formatEuro(change) }) : t('v2.pay')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CashChangeSheet;
